import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Check, Clock, Mail, Users, CreditCard } from 'lucide-react';
import PillarLayout from '../pillar/PillarLayout';
import CheckoutModal from '../CheckoutModal';

const CANONICAL = 'https://enbet-consulting.de/ki-strategie-workshop';

const bullets = [
  { t: 'KI-Strategie mit Rolloutplan', s: 'Das konkrete, strategische Fundament inklusive klarem Zeitrahmen.' },
  { t: 'Interaktives Use-Case-Mapping', s: 'Gemeinsame Priorisierung nach wirtschaftlichem Hebel und Machbarkeit.' },
  { t: 'Validierte ROI-Kalkulation', s: 'Fundierte Berechnung des jährlichen Einsparpotenzials im Verhältnis zu den Einführungskosten.' },
  { t: 'Pragmatische 3-Phasen-Roadmap', s: 'Chronologischer Fahrplan für die interne Einführung und Umsetzung.' },
  { t: 'Konkrete Quick-Wins', s: 'Sofort umsetzbare Optimierungsimpulse, die ohne langwierige IT-Projekte direkt Entlastung bringen.' },
];

const steps = [
  { n: '01', t: 'Voraussetzung: KI Ready Check', s: 'Der Workshop setzt einen abgeschlossenen KI Ready Check (Modul 1) voraus. Die erhobenen Verlustzeiten sind die Datenbasis.' },
  { n: '02', t: '1–2 Wochen Ausarbeitung', s: 'Sebastian verdichtet die Erkenntnisse aus dem Ready Check zu konkreten Hypothesen, Use-Case-Kandidaten und einer vorbereiteten ROI-Logik.' },
  { n: '03', t: 'Workshop-Tag', s: 'Interaktiver Tages-Workshop mit euren Entscheidern (typischerweise 4–6 Personen) – gemeinsame Priorisierung, Roadmap-Festlegung, Quick-Win-Auswahl.' },
  { n: '04', t: 'Nachbereitung + Roadmap', s: 'Finalisierter Audit-Report, validierte ROI-Kalkulation, 3-Phasen-Plan – nutzbar als Investitionsentscheidungs-Vorlage.' },
];

const faqItems = [
  {
    q: 'Was kostet der KI Strategie Workshop?',
    a: 'Der Workshop kostet 3.500 € als Festpreis. Enthalten: 1–2 Wochen Ausarbeitung, interaktiver Workshop-Tag mit euren Entscheidern (4–6 Personen), validierte ROI-Kalkulation, pragmatische 3-Phasen-Roadmap, konkrete Quick-Wins, strukturierter Audit-Report.',
  },
  {
    q: 'Warum ist der Ready Check Voraussetzung?',
    a: 'Eine Strategie ohne datenbasierte Standortbestimmung ist Bauchgefühl. Der Ready Check liefert die quantifizierten Verlustzeiten, ohne die ROI-Kalkulation reine Spekulation wäre. Wir machen den Workshop nicht ohne diese Datenbasis – egal wie sehr ein Kunde drängt.',
  },
  {
    q: 'Wie viele Personen sollten am Workshop teilnehmen?',
    a: 'Optimal sind 4–6 Entscheider: Geschäftsführung + 1–2 Bereichsleiter + ggf. 1–2 Frontline-Verantwortliche. Mehr als 8 macht den Workshop ineffizient, weniger als 3 reduziert die Tragweite der Entscheidungen.',
  },
  {
    q: 'Findet der Workshop vor Ort oder remote statt?',
    a: 'Beides möglich. Empfehlung: Tag vor Ort in eurem Betrieb (Kassel-Region) oder bei Sebastian in Schauenburg, dann beste Workshop-Dynamik. Reine Online-Workshops sind möglich, aber die Entscheidungs-Tiefe ist erfahrungsgemäß geringer.',
  },
  {
    q: 'Was bekomme ich konkret als Ergebnis?',
    a: 'Einen validierten Audit-Report (15–25 Seiten PDF), eine ROI-Kalkulation mit Sensitivitätsanalyse, eine 3-Phasen-Roadmap mit klaren Verantwortlichen und Meilensteinen, sowie 2–3 priorisierte Quick-Wins für die ersten 4–6 Wochen.',
  },
];

export default function LandingStrategieWorkshop() {
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const schemaLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Service',
        '@id': `${CANONICAL}#service`,
        name: 'KI Strategie Workshop',
        provider: { '@id': 'https://enbet-consulting.de/#organization' },
        serviceType: 'Strategischer KI-Workshop mit Roadmap und ROI-Kalkulation',
        description: 'Aus den Erkenntnissen des Ready Check entsteht eine konkrete KI-Roadmap mit validierter ROI-Logik, Quick-Wins und 3-Phasen-Plan. Festpreis 3.500 €.',
        offers: {
          '@type': 'Offer',
          price: '3500',
          priceCurrency: 'EUR',
          availability: 'https://schema.org/InStock',
          url: CANONICAL,
        },
        areaServed: 'DE',
        inLanguage: 'de-DE',
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Start', item: 'https://enbet-consulting.de/' },
          { '@type': 'ListItem', position: 2, name: 'KI Strategie Workshop', item: CANONICAL },
        ],
      },
      {
        '@type': 'FAQPage',
        mainEntity: faqItems.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      },
    ],
  };

  return (
    <PillarLayout
      title="KI Strategie Workshop – Roadmap, ROI & Quick-Wins | ENBET Consulting"
      description="Der KI Strategie Workshop überführt die Erkenntnisse des Ready Check in eine konkrete Roadmap. ROI-Kalkulation, 3-Phasen-Plan, Quick-Wins. Festpreis 3.500 €."
      canonical={CANONICAL}
      schemaLd={schemaLd}
    >
      {/* Hero */}
      <section className="bg-[#0a0a0a] pt-32 pb-20 md:pt-40 md:pb-28">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="text-[#befa0a] text-xs uppercase tracking-[0.3em] mb-5 font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Modul 02 · KI Strategie Workshop
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
            className="text-white text-3xl md:text-5xl lg:text-6xl font-bold uppercase tracking-tight leading-[1.1] mb-6" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Strategie ist keine PowerPoint.
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.25 }}
            className="text-white/70 text-base md:text-lg leading-relaxed max-w-2xl mb-8" style={{ fontFamily: "'Manrope', sans-serif" }}>
            Strategie ist eine Entscheidung – mit Zahlen, Verantwortlichen und Datum. Im Workshop überführen wir die Erkenntnisse aus eurem KI Ready Check in eine Roadmap, die euer Team mitträgt. Ein Tag, vier Bausteine, keine Folien-Lyrik.
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.4 }}
            className="inline-flex flex-col items-start gap-1 border border-[#befa0a]/30 bg-[#befa0a]/5 px-6 py-4 mb-8">
            <div className="inline-flex items-baseline gap-4">
              <span className="text-[#befa0a] text-3xl md:text-4xl font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>3.500 €</span>
              <span className="text-white/60 text-sm uppercase tracking-wider" style={{ fontFamily: "'Oswald', sans-serif" }}>Festpreis · Workshop-Tag inkl. Vor- &amp; Nachbereitung</span>
            </div>
            <span className="text-white/40 text-[11px]">netto, zzgl. 19 % MwSt</span>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.5 }}
            className="flex flex-col sm:flex-row gap-4">
            <Link to="/#booking"
              className="inline-flex items-center justify-center gap-2 bg-[#befa0a] text-[#0a0a0a] px-8 py-4 text-sm font-bold uppercase tracking-[0.15em] hover:bg-white transition-colors"
              style={{ fontFamily: "'Oswald', sans-serif" }}>
              <Check size={16} /> Kostenloses Erstgespräch
            </Link>
            <button type="button" onClick={() => setCheckoutOpen(true)}
              className="inline-flex items-center justify-center gap-2 border border-white/30 text-white px-8 py-4 text-sm font-bold uppercase tracking-[0.15em] hover:bg-white hover:text-[#0a0a0a] transition-colors"
              style={{ fontFamily: "'Oswald', sans-serif" }}>
              <CreditCard size={16} /> Direkt buchen (3.500 €)
            </button>
          </motion.div>
          <p className="text-white/40 text-xs mt-3" style={{ fontFamily: "'Manrope', sans-serif" }}>
            Sichere Bezahlung über Mollie · SEPA · Kreditkarte · Klarna · Voraussetzung: KI Ready Check
          </p>
          <p className="text-white/60 text-sm mt-6" style={{ fontFamily: "'Manrope', sans-serif" }}>
            Noch keinen Ready Check?{' '}
            <Link to="/ki-kombi-paket" className="text-[#befa0a] hover:underline font-bold">
              Kombi-Paket Modul 1 + 2 für 2.900 € netto (1.450 € sparen) →
            </Link>
          </p>
        </div>
      </section>

      {/* Pain / Anchor */}
      <section className="bg-[#f7f7f7] py-16 md:py-20">
        <div className="max-w-[900px] mx-auto px-6 md:px-10">
          <p className="text-[#e07a2f] text-xs uppercase tracking-[0.25em] font-bold mb-4" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Warum ein Workshop, kein Report
          </p>
          <h2 className="text-[#1f1f1f] text-2xl md:text-4xl font-bold uppercase tracking-tight leading-[1.15] mb-6" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Ein Report bringt nur etwas, wenn er ankommt.
          </h2>
          <p className="text-[#1f1f1f]/75 text-base md:text-lg leading-relaxed mb-5" style={{ fontFamily: "'Manrope', sans-serif" }}>
            Wir hätten dir auch einfach ein Strategiepapier schicken können. Aber Strategie-PDFs landen in Outlook-Ordnern. Strategie-Entscheidungen entstehen am Tisch.
          </p>
          <p className="text-[#1f1f1f]/65 text-base leading-relaxed" style={{ fontFamily: "'Manrope', sans-serif" }}>
            Im Workshop bringen wir die Schlüsselpersonen zusammen – Geschäftsführung, Bereichsleiter, ggf. Frontline-Verantwortliche. Gemeinsam priorisieren wir die Use-Cases, rechnen den ROI durch, legen Verantwortliche fest. Was rauskommt, ist kein Papier, sondern eine Entscheidung mit Datum.
          </p>
        </div>
      </section>

      {/* Bausteine */}
      <section className="py-16 md:py-24">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10">
          <p className="text-[#e07a2f] text-xs uppercase tracking-[0.25em] font-bold mb-4" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Bausteine
          </p>
          <h2 className="text-[#1f1f1f] text-2xl md:text-4xl font-bold uppercase tracking-tight leading-tight mb-12" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Fünf Bausteine, eine Roadmap mit Datum.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {bullets.map((b, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="border border-[#e5e5e5] p-6 flex gap-4">
                <div className="w-10 h-10 rounded-full bg-[#0a0a0a] text-[#befa0a] flex items-center justify-center flex-shrink-0 text-sm font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>
                  {String(i + 1).padStart(2, '0')}
                </div>
                <div className="flex-1">
                  <h3 className="text-[#1f1f1f] text-base md:text-lg font-bold uppercase tracking-tight mb-2" style={{ fontFamily: "'Oswald', sans-serif" }}>{b.t}</h3>
                  <p className="text-[#1f1f1f]/70 text-sm md:text-base leading-relaxed" style={{ fontFamily: "'Manrope', sans-serif" }}>{b.s}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Prozess */}
      <section className="bg-[#0a0a0a] py-16 md:py-24">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10">
          <p className="text-[#befa0a] text-xs uppercase tracking-[0.25em] font-bold mb-4" style={{ fontFamily: "'Oswald', sans-serif" }}>
            So läuft's ab
          </p>
          <h2 className="text-white text-2xl md:text-4xl font-bold uppercase tracking-tight leading-tight mb-12" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Vier Schritte zur entscheidungsreifen Strategie.
          </h2>
          <div className="space-y-6">
            {steps.map((s, i) => (
              <motion.div key={i} initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="flex gap-6 border-b border-[#1a1a1a] pb-6">
                <span className="text-[#befa0a] text-3xl md:text-4xl font-bold flex-shrink-0" style={{ fontFamily: "'Oswald', sans-serif" }}>{s.n}</span>
                <div className="flex-1">
                  <h3 className="text-white text-lg md:text-xl font-bold uppercase tracking-tight mb-2" style={{ fontFamily: "'Oswald', sans-serif" }}>{s.t}</h3>
                  <p className="text-white/65 text-base leading-relaxed" style={{ fontFamily: "'Manrope', sans-serif" }}>{s.s}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-[#f5f5f5] py-16 md:py-24">
        <div className="max-w-[900px] mx-auto px-6 md:px-10">
          <p className="text-[#e07a2f] text-xs uppercase tracking-[0.25em] font-bold mb-4" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Häufige Fragen
          </p>
          <h2 className="text-[#1f1f1f] text-2xl md:text-4xl font-bold uppercase tracking-tight leading-tight mb-10" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Was du wahrscheinlich noch fragst.
          </h2>
          <div className="space-y-8">
            {faqItems.map((f, i) => (
              <div key={i}>
                <h3 className="text-[#1f1f1f] text-lg md:text-xl font-bold uppercase tracking-tight mb-3" style={{ fontFamily: "'Oswald', sans-serif" }}>{f.q}</h3>
                <p className="text-[#1f1f1f]/70 text-base leading-relaxed" style={{ fontFamily: "'Manrope', sans-serif" }}>{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-[#0a0a0a] py-16 md:py-20">
        <div className="max-w-[900px] mx-auto px-6 md:px-10 text-center">
          <h2 className="text-white text-2xl md:text-4xl font-bold uppercase tracking-tight leading-tight mb-6" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Aus Erkenntnis wird Entscheidung.
          </h2>
          <p className="text-white/60 text-base md:text-lg mb-8 max-w-xl mx-auto" style={{ fontFamily: "'Manrope', sans-serif" }}>
            Erst Ready Check, dann Workshop. So bauen wir auf echte Daten statt auf Bauchgefühl – und ihr bekommt eine Strategie, die euer Team mitträgt.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/#booking"
              className="inline-flex items-center justify-center gap-2 bg-[#befa0a] text-[#0a0a0a] px-8 py-4 text-sm font-bold uppercase tracking-[0.15em] hover:bg-white transition-colors"
              style={{ fontFamily: "'Oswald', sans-serif" }}>
              <Check size={16} /> Erstgespräch buchen
            </Link>
            <button type="button" onClick={() => setCheckoutOpen(true)}
              className="inline-flex items-center justify-center gap-2 border border-white/30 text-white px-8 py-4 text-sm font-bold uppercase tracking-[0.15em] hover:bg-white hover:text-[#0a0a0a] transition-colors"
              style={{ fontFamily: "'Oswald', sans-serif" }}>
              <CreditCard size={16} /> Direkt buchen (3.500 €)
            </button>
          </div>
          <div className="mt-12 pt-8 border-t border-[#1a1a1a] flex flex-wrap items-center justify-center gap-3 text-white/40 text-sm" style={{ fontFamily: "'Manrope', sans-serif" }}>
            <Clock size={14} /> 1–2 Wochen + 1 Workshop-Tag
            <span className="text-white/20">·</span>
            <Users size={14} /> 4–6 Entscheider
            <span className="text-white/20">·</span>
            <span>3.500 € Festpreis</span>
          </div>
        </div>
      </section>

      {/* Weiterführende Links */}
      <section className="py-12 bg-white">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10">
          <p className="text-[#1f1f1f]/50 text-xs uppercase tracking-[0.25em] font-bold mb-5" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Verwandte Themen
          </p>
          <div className="flex flex-wrap gap-3">
            {[
              { to: '/ki-ready-check', label: 'KI Ready Check (Modul 1, Voraussetzung)' },
              { to: '/ki-strategie-retail', label: 'KI-Strategie Retail – Guide' },
              { to: '/ki-fuer-filialisten', label: 'KI für Filialisten' },
              { to: '/ki-beratung-kassel', label: 'KI-Beratung Kassel' },
            ].map((l, i) => (
              <Link key={i} to={l.to}
                className="inline-flex items-center gap-2 border border-[#e5e5e5] text-[#1f1f1f]/70 hover:border-[#befa0a] hover:text-[#1f1f1f] px-4 py-2 text-xs uppercase tracking-wider transition-colors"
                style={{ fontFamily: "'Oswald', sans-serif" }}>
                {l.label} <ArrowRight size={12} />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CheckoutModal
        open={checkoutOpen}
        onClose={() => setCheckoutOpen(false)}
        module="strategy"
        productName="KI Strategie Workshop"
        amountLabel="3.500 €"
      />
    </PillarLayout>
  );
}
